import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import * as sfx from './sfx';

const CHAPTERS = [
  { id: 'ch-1', num: '01', title: 'Raw Leather' },
  { id: 'about-intro', num: null, title: 'About Us' },
  { id: 'ch-2', num: '02', title: 'Selection' },
  { id: 'ch-3', num: '03', title: 'Pattern Making' },
  { id: 'ch-4', num: '04', title: 'Cutting' },
  { id: 'ch-5', num: '05', title: 'Stitching' },
  { id: 'ch-6', num: '06', title: 'Hardware' },
  { id: 'ch-7', num: '07', title: 'Inspection' },
  { id: 'ch-8', num: '08', title: 'The Final Piece' },
  { id: 'ch-11', num: '09', title: 'About the Company' },
  { id: 'ch-9', num: '10', title: 'Global Manufacturing' },
  { id: 'ch-archive', num: '11', title: 'From the Archive' },
  { id: 'ch-10', num: '12', title: 'Partner With Us' },
];

export const ChapterNav = () => {
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(false);

  useEffect(() => {
    const init = sfx.initialEnabled();
    setSound(init);
    sfx.setEnabled(init);
  }, []);

  useEffect(() => {
    let acc = 0;
    let lastY = window.scrollY;
    let lastT = 0;
    const onScroll = () => {
      const y = window.scrollY;
      acc += Math.abs(y - lastY);
      lastY = y;
      const now = performance.now();
      if (acc > 170 && now - lastT > 80) { sfx.stitch(); acc = 0; lastT = now; }
    };
    let lastHover = 0;
    const onOver = (e) => {
      if (!e.target.closest('a, button, [data-hover]')) return;
      const now = performance.now();
      if (now - lastHover > 180) { sfx.snap(); lastHover = now; }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseover', onOver);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseover', onOver);
    };
  }, []);

  const toggleSound = () => {
    const v = !sound;
    setSound(v);
    sfx.setEnabled(v);
    if (v) sfx.snap();
  };

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.6 });
      else el.scrollIntoView({ behavior: 'smooth' });
    }, 350);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[80] flex items-center justify-between px-6 md:px-12 py-5">
        <button
          data-testid="nav-logo"
          onClick={() => goTo('ch-1')}
          className="font-display text-xl md:text-2xl tracking-wide text-[#F5F5F0] bg-transparent border-0"
        >
          PT <span className="italic text-[#B89B72]">Exports</span>
        </button>
        <div className="flex items-center gap-3">
          <button
            data-testid="sound-toggle"
            onClick={toggleSound}
            aria-label={sound ? 'Mute craft sounds' : 'Enable craft sounds'}
            title="Craft sounds — soft leather, stitching & zipper"
            className={`flex items-center justify-center p-3 backdrop-blur-xl border rounded-full w-12 h-12 transition-colors duration-300 ${sound ? 'bg-[#8C5A35]/30 border-[#B89B72]/60 text-[#B89B72]' : 'bg-[#0A0A0A]/40 border-[#F5F5F0]/10 text-[#A3A3A3]'}`}
          >
            {sound ? <Volume2 className="w-4 h-4" strokeWidth={1.4} /> : <VolumeX className="w-4 h-4" strokeWidth={1.4} />}
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => { if (!open) sfx.zipper(); setOpen(!open); }}
            aria-label="Menu"
            className="flex flex-col items-end gap-[6px] p-3 bg-[#0A0A0A]/40 backdrop-blur-xl border border-[#F5F5F0]/10 rounded-full w-12 h-12 justify-center"
          >
            <motion.span animate={{ rotate: open ? 45 : 0, y: open ? 4 : 0, width: open ? 20 : 20 }} className="block h-px bg-[#B89B72]" style={{ width: 20 }} />
            <motion.span animate={{ rotate: open ? -45 : 0, y: open ? -3 : 0 }} className="block h-px bg-[#F5F5F0]" style={{ width: 14 }} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="chapter-menu"
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[75] bg-[#0d0b09] flex flex-col justify-center px-8 md:px-24 overflow-y-auto"
          >
            <span className="font-accent text-[10px] text-[#8C5A35] mb-8 block">The Making of a Jacket — Index</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-1 max-h-[70vh]">
              {CHAPTERS.map((ch, i) => (
                <motion.button
                  key={ch.id}
                  data-testid={`menu-${ch.id}`}
                  initial={{ opacity: 0, rotateX: -90, y: -20 }}
                  animate={{ opacity: 1, rotateX: 0, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.07, duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
                  onClick={() => goTo(ch.id)}
                  className="group flex items-baseline gap-5 py-2.5 md:py-3 text-left bg-transparent border-0 border-b border-[#2A2A2A]"
                  style={{ transformOrigin: 'top center', perspective: '600px' }}
                >
                  <span className="font-accent text-[10px] text-[#8C5A35]">{ch.num ? `CH. ${ch.num}` : 'INTRO'}</span>
                  <span className="font-display text-2xl md:text-4xl font-light text-[#F5F5F0]/80 group-hover:text-[#B89B72] group-hover:italic transition-colors duration-300">
                    {ch.title}
                  </span>
                </motion.button>
              ))}
            </div>
            <div className="mt-10 font-body text-xs text-[#525252] tracking-wide">
              Pakkar Tanveer Exports — 35+ Years of Craftsmanship
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChapterNav;
